import { Link } from 'react-router-dom';
import { LEGAL, CLICK_DEDUP_WINDOW_MS } from '@affily/shared/constants';
import { Seo } from '../components/Seo.jsx';
import { LegalPage, LegalSection, LegalList, ContactLink } from '../components/Legal.jsx';

/**
 * Privacy Policy.
 *
 * DRAFT. Every statement below was checked against the code (models, the click
 * redirect, auth, leads, uploads, Stripe routes, morgan access logging). Where
 * the code does not enforce a retention period the text says so rather than
 * inventing one. Entity name and contact address live in LEGAL
 * (shared/src/constants.js) and are unconfirmed. Not reviewed by a lawyer.
 */
export default function Privacy() {
  const dedupHours = Math.round(CLICK_DEDUP_WINDOW_MS / 3_600_000);

  return (
    <>
      <Seo
        title="Privacy Policy"
        description="What personal data Affily collects, why, who we share it with, how long we keep it, the cookies we set, and how to ask us to access, correct or delete your data."
      />

      <LegalPage
        title="Privacy Policy"
        intro="What we collect, why, who else touches it, and how to get it corrected or deleted."
        summary={[
          'We collect what an account needs: email, name, a hashed password (or Google, Facebook or Instagram sign-in), and the profile you choose to publish.',
          'When someone clicks a creator’s tracked link, we store a scrambled (hashed) version of their IP address and browser, not the raw values, plus the referring page and country if available. Our server logs may still show raw IPs.',
          'Payments go through Stripe. We never see or store full card numbers or bank details.',
          'If you connect TikTok, Instagram, YouTube or a Facebook Page, we read your follower count and video numbers from it and show them as verified. Disconnecting deletes our access straight away.',
          'We use two cookies: one to keep you signed in and one that remembers which tracked link brought you to a brand. No advertising or analytics cookies.',
          'If you give us your email in a free tool, we keep it.',
          'You can ask to see, correct or delete your data by emailing us (section 7 says how).',
          'Affily is for people aged 18 and over.',
        ]}
      >
        <LegalSection title="1. Who is responsible">
          <p>
            {LEGAL.entityName} (“we”, “us”) runs Affily and decides how personal data on it is used
            (the “controller” in data-protection terms). Contact: <ContactLink />. This policy sits
            alongside our{' '}
            <Link to="/terms" className="text-accent underline underline-offset-2">
              Terms of Service
            </Link>
            .
          </p>
          <p>
            One role worth knowing about: a brand that reports conversions to us is responsible for
            the data it sends us about its own customers. We recommend brands send only an order or
            subscription identifier, never a customer’s name or email address.
          </p>
        </LegalSection>

        <LegalSection title="2. What we collect">
          <p>
            <strong className="text-ink">Account data.</strong> Email address, name, your roles
            (creator, brand or both), whether the email is confirmed, when you last signed in, and
            an avatar URL if you set one. A password is stored only as a bcrypt hash; we cannot read
            it. If you sign in with Google, Facebook or Instagram we store that account’s
            identifier, and the name, picture and (Google and Facebook only) confirmed email it
            gives us. For signing in we keep none of their access tokens: each is used once, to ask
            who you are. Instagram never shares an email, so you give us one and confirm it.
            Connecting an account to your creator profile is different, and is described next.
          </p>
          <p id="connected-accounts" className="scroll-mt-24">
            <strong className="text-ink">Connected social accounts.</strong> A creator can connect
            TikTok, Instagram (a business or creator account), YouTube or a Facebook Page, in their
            dashboard. You approve Affily on that platform, and we then read, with read-only access:
          </p>
          <LegalList>
            <li>
              the account’s identifier, username or channel handle, display name, profile picture
              and profile link;
            </li>
            <li>
              its follower or subscriber count, and the views, likes and comments of its latest 12
              videos, from which we work out average views and an engagement rate (we keep the
              averages, not the individual videos);
            </li>
            <li>
              for a competition you enter: the videos you choose to enter (link, title, thumbnail,
              when posted) and their views, likes, comments and shares, read again while the
              competition runs.
            </li>
          </LegalList>
          <p>
            We never post, comment, send messages or change anything on your account, and we do not
            read your private messages, followers’ identities or audience demographics. The access
            tokens the platform gives us are stored encrypted (AES-256-GCM) and used only for the
            reads above: once when you connect, then about once a day for your follower numbers, and
            more often for entered competition videos. The numbers appear on your profile labelled
            verified, and you cannot edit them by hand while the account is connected. The
            permissions we ask for are: TikTok user.info.basic, user.info.profile, user.info.stats
            and video.list; Instagram instagram_business_basic and instagram_business_manage_insights;
            YouTube youtube.readonly and yt-analytics.readonly; Facebook pages_show_list and
            pages_read_engagement.
          </p>
          <p>
            <strong className="text-ink">Google and YouTube.</strong> Connecting YouTube uses the
            YouTube API Services, so the{' '}
            <a
              href="https://www.youtube.com/t/terms"
              className="text-accent underline underline-offset-2"
              rel="noopener noreferrer"
            >
              YouTube Terms of Service
            </a>{' '}
            and the{' '}
            <a
              href="https://policies.google.com/privacy"
              className="text-accent underline underline-offset-2"
              rel="noopener noreferrer"
            >
              Google Privacy Policy
            </a>{' '}
            apply to that connection as well. Affily’s use and transfer to any other app of
            information received from Google APIs will adhere to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              className="text-accent underline underline-offset-2"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements. We use YouTube data only to show your channel’s
            numbers on your Affily profile and to measure the competition videos you enter; we do
            not use it for advertising, sell it, or use it to train AI models. You can remove
            Affily’s access at any time from your{' '}
            <a
              href="https://myaccount.google.com/permissions"
              className="text-accent underline underline-offset-2"
              rel="noopener noreferrer"
            >
              Google account’s third-party access page
            </a>
            , as well as by disconnecting in Affily.
          </p>
          <p>
            <strong className="text-ink">Profile data you publish.</strong> For creators: display
            name, headline, bio, avatar, niches, social platform handles and links, follower counts,
            average views, engagement rate, audience demographics and starting rates. For brands:
            company name, website, logo, description, categories and location. Programs, applications
            (including the message you write), partnerships and reviews are also stored.
            Once you publish, a profile and its contents are public to anyone, including search
            engines. Audience figures you type in are labelled self-reported; those read from a
            connected account are labelled verified.
          </p>
          <p>
            <strong className="text-ink">Uploads.</strong> Images and video you upload are stored on
            our server under a name derived from the file’s contents, and are served from a public
            address. Anyone who has that address can open the file, whether or not your profile is
            published. We do not remove metadata such as camera location from files you upload,
            so strip it first if it matters.
          </p>
          <p>
            <strong className="text-ink">Click and conversion data.</strong> When someone opens a
            tracked link (affily.me/go/…), we record a click row containing: a hashed version of
            their IP address and of their browser’s user-agent string, the page that referred them
            (up to 500 characters of the referring URL), a two-letter country code when our network
            provider supplies one, which link and program it was, and the time. We do not store the
            raw IP address or user-agent in the click record. The hashes are created with a
            secret key held on our server, so they cannot be reversed by guessing, but they stay
            consistent so we can tell repeat visits apart and match a later sale to a click. A short
            separate record of those hashes drives a {dedupHours}-hour repeat-click check and expires
            automatically after about {dedupHours * 2} hours. Because the referring URL is stored as
            sent, it can contain whatever the referring site put in it.
          </p>
          <p>
            Conversions reported by a brand (through a connected Stripe, Shopify or WooCommerce
            account, webhooks, signed server-to-server callbacks or the browser tag) store the brand’s order identifier, the
            order amount and currency, a subscription identifier for recurring programs, the click it
            was matched to, and how it was matched.
          </p>
          <p>
            <strong className="text-ink">Gift delivery details.</strong> When you request a gift
            that needs them, we collect a postal address (your name, address lines, city, region,
            postal code, country and, optionally, a phone number) or, for a booking, a note with
            dates and names. We collect these only for that request, never for a gift that needs
            nothing. The country alone is visible to the brand while it decides. The rest is visible
            only to the brand that runs that program, and only from the moment it approves the
            request. It is never shown on your public profile, to other brands, or in any email.
            It is stored separately from the request itself.
          </p>
          <p>
            <strong className="text-ink">Free-tool emails.</strong> The media kit generator asks for
            an email before you download your PDF. We store the address, the name if you gave one,
            which tool it came from, and a small summary of what you built (your total followers and
            which platforms). The kit itself is built in your browser and is not uploaded. The
            engagement rate calculator does not collect an email.
          </p>
          <p>
            <strong className="text-ink">Payments data.</strong> We store Stripe identifiers (a
            connected-account ID for creators; a customer ID, a payment-method ID and a display
            label such as card type and last four digits for brands), and our own records of
            commissions, payouts and fees. Card numbers, bank details and identity documents are
            collected and held by Stripe, not by us.
          </p>
          <p>
            <strong className="text-ink">API keys and integration secrets.</strong> For an API key we
            store its name, a visible prefix, a SHA-256 hash of the key, its scopes, and when it
            was last used. If a brand connects Stripe, Shopify or WooCommerce for conversion
            tracking, we store what that connection needs: the Stripe account ID, a Shopify access
            token or WooCommerce API keys (both encrypted), or the webhook signing secret the brand
            gives us. Each program also has its own signing secret. We do not store shoppers’ names,
            email addresses or postal addresses from the orders we receive.
          </p>
          <p>
            <strong className="text-ink">Technical data.</strong> Our web server writes standard
            access logs in production that include IP address, browser user-agent, requested
            address, referrer and status code. Our host and any network provider in front of us can
            keep similar logs. Unlike the click record, these logs can contain raw IP addresses.
          </p>
        </LegalSection>

        <LegalSection title="3. Why we use it">
          <LegalList>
            <li>
              <strong className="text-ink">To provide Affily</strong> (contract): accounts,
              profiles, programs, tracking links, attribution, commissions, settling and payouts.
            </li>
            <li>
              <strong className="text-ink">To show verified numbers</strong> (contract, at your
              request): reading a connected social account’s followers and video numbers onto your
              profile, and measuring competition videos you enter.
            </li>
            <li>
              <strong className="text-ink">To send service emails</strong> (contract): a draft is
              ready to review, someone applied to your program, an application was accepted or
              declined, a payout is on its way. We do not send advertising from the platform
              itself.
            </li>
            <li>
              <strong className="text-ink">To keep Affily safe and fair</strong> (legitimate
              interests): spotting fake or repeated clicks, forged conversions and abuse, rate
              limiting, security and debugging.
            </li>
            <li>
              <strong className="text-ink">To contact people who used a free tool</strong>{' '}
              (legitimate interests or your consent, depending on where you are): to tell you when
              something relevant ships.
            </li>
            <li>
              <strong className="text-ink">To meet legal obligations</strong>, such as tax,
              accounting, sanctions and responding to lawful requests.
            </li>
          </LegalList>
          <p>
            We do not sell personal data, and we do not use it for advertising profiles.
          </p>
        </LegalSection>

        <LegalSection title="4. Who we share it with">
          <p>These companies process data for us or because you asked us to use them.</p>
          <LegalList>
            <li>
              <strong className="text-ink">Stripe</strong>: payment processing, creator payouts
              (Stripe Connect) and saving a brand’s card for later charges. Stripe is also an
              independent controller of the data it collects to verify you and prevent fraud, and
              its own privacy policy applies. Stripe.js loads in your browser on the billing screen
              and may set its own cookies.
            </li>
            <li>
              <strong className="text-ink">Cloudflare</strong>: R2 object storage for the images
              and videos you upload (avatars, logos, portfolio files). Uploaded files are served
              publicly from a Cloudflare-hosted address, so anyone with the link can see them.
            </li>
            <li>
              <strong className="text-ink">Zoho (Zoho Mail)</strong>: sends our service emails, so
              it sees recipient addresses and message contents.
            </li>
            <li>
              <strong className="text-ink">Google</strong>, only if Google sign-in is switched on:
              the sign-in button loads Google’s script from Google, which means Google sees your
              visit to the sign-in and sign-up pages.
            </li>
            <li>
              <strong className="text-ink">Meta (Facebook and Instagram)</strong>, only if you choose
              to sign in with either, or connect an Instagram account or Facebook Page: you approve
              Affily on their site, and they tell us who you are and, for a connection, the numbers
              described in section 2. They learn that you use Affily.
            </li>
            <li>
              <strong className="text-ink">TikTok and Google (YouTube)</strong>, only if you connect
              an account there: the same as above. Google’s and TikTok’s own privacy policies cover
              what they do.
            </li>
            <li>
              <strong className="text-ink">Shopify, WooCommerce and Stripe</strong>, only if a brand
              connects its store: we read the orders it sends us and create creator discount codes in
              it, as described in section 2.
            </li>
            <li>
              <strong className="text-ink">Hetzner</strong> (Hetzner Online GmbH, in the European
              Union): our server and MongoDB database run on a server we rent from Hetzner, which
              stores everything described above on our behalf. Uploaded files are with Cloudflare,
              above.
            </li>
            <li>
              <strong className="text-ink">The other side of a deal.</strong> A brand sees an
              applying creator’s profile, application message and tracked performance. A creator
              sees a brand’s program, terms and payment record with them. Click and conversion
              statistics are shown in aggregate and per link; individual visitors’ hashed
              identifiers are not shown. If you request a gift, the brand running that program also
              sees the delivery details for it, once it approves the request (section 2).
            </li>
            <li>
              <strong className="text-ink">The public</strong>, for anything you publish (section 2),
              and for reviews.
            </li>
            <li>
              <strong className="text-ink">Authorities and advisers</strong> where the law requires
              it, to protect our rights, or in a sale or restructuring of the business.
            </li>
          </LegalList>
          <p>
            Some of these providers are outside your country, including in the United States
            (Stripe, Cloudflare, Zoho, Google, Meta, TikTok). Where personal data leaves the
            European Economic Area, we rely on the EU–US Data Privacy Framework for providers
            certified under it, and otherwise on the standard contractual clauses in that provider’s
            data processing terms.
          </p>
        </LegalSection>

        <LegalSection title="5. Cookies and local storage">
          <p>We set two cookies and use no advertising or analytics cookies.</p>
          <LegalList>
            <li>
              <strong className="text-ink">affily_token</strong>: keeps you signed in. It is
              HTTP-only (scripts on the page cannot read it), SameSite=Lax, marked Secure in
              production, and lasts 7 days. Strictly necessary.
            </li>
            <li>
              <strong className="text-ink">aff_click</strong>: set when you follow a creator’s
              tracked link. It holds the identifier of that click and lasts as long as the
              program’s cookie window (set by the brand, between 1 and 365 days), and exists so a
              later purchase can be matched to the click that led to it. This one is readable by
              scripts and is SameSite=Lax. You can delete it in your browser; the link’s click
              identifier is also added to the address of the page you land on.
            </li>
          </LegalList>
          <p>
            Our own site code does not use localStorage or sessionStorage. Brands that follow our
            integration guides may store the click identifier on their own site, under their own
            privacy policy. Third parties we load (Google sign-in, Stripe.js) may set cookies of
            their own, which are covered by their policies.
          </p>
        </LegalSection>

        <LegalSection title="6. How long we keep it">
          <p>
            We would rather say plainly what we do than promise automated deletion we have not
            built.
          </p>
          <LegalList>
            <li>
              The repeat-click check records expire automatically after about {dedupHours * 2}{' '}
              hours.
            </li>
            <li>
              Gift delivery details (postal addresses and booking notes) are deleted automatically
              and straight away when a gift is marked received, or the request is declined or
              cancelled. Otherwise they are deleted 30 days after the brand marks the gift as
              shipped, and in any case no later than 120 days after the request. The record that a
              request happened (the gift, its status and dates) stays, without the address.
            </li>
            <li>
              A connected social account’s access tokens are deleted the moment you disconnect it.
              The numbers we read from it are removed from your profile at the same time. If a read
              fails, for example because you withdrew access on the platform, the
              account shows as needing reconnecting, and we try again once a day until you reconnect
              or disconnect it.
            </li>
            <li>
              When a brand uninstalls the Shopify app, Shopify tells us, and we delete that shop’s
              connection record 48 hours later. Disconnecting Stripe or WooCommerce deletes our
              access to it straight away.
            </li>
            <li>
              Everything else (account, profile, click and conversion records, commissions,
              payouts, reviews, leads, uploads) is kept while your account is active and is not
              currently deleted on a timer. We delete or anonymise it on request (section 7), except
              what we must keep: payment, payout and tax records, and records needed to resolve
              disputes or prevent fraud.
            </li>
            <li>
              Commission, payout and partnership records are shared between a creator and a brand
              and may need to be kept for both sides’ accounting even after one of them leaves.
            </li>
            <li>
              Server access logs are kept in a rolling buffer of about 30 MB, so older lines are
              overwritten as new ones arrive. Database backups are kept for up to 14 days.
            </li>
            <li>
              Email addresses from free tools are kept until you ask us to delete them, or until the
              address becomes an account.
            </li>
          </LegalList>
        </LegalSection>

        <LegalSection title="7. Your rights, and deleting your data" id="delete">
          <p>
            Depending on where you live (for example under the EU GDPR, the UK GDPR, or California
            and other US state privacy laws), you can ask us to:
          </p>
          <LegalList>
            <li>tell you what personal data we hold about you and give you a copy;</li>
            <li>correct data that is wrong (you can edit your profile yourself at any time);</li>
            <li>delete your data;</li>
            <li>restrict or object to some uses, including contact from a free-tool signup;</li>
            <li>give you your data in a portable format;</li>
            <li>
              withdraw consent where we rely on it. Withdrawing does not affect what we did before.
            </li>
          </LegalList>
          <p>
            <strong className="text-ink">To remove a connected account</strong>, open your creator
            profile in your dashboard and press Disconnect next to it: our access tokens are deleted
            straight away. You can also withdraw Affily’s access on the platform itself: in
            Instagram’s or Facebook’s settings under apps and websites, on your Google account’s
            third-party access page, or in TikTok’s settings under security and app permissions.
          </p>
          <p>
            <strong className="text-ink">To delete everything</strong>, or for any request above,
            email <ContactLink /> from the address on your account. We will
            check it is you, and we aim to answer within one month. At the moment there is no
            self-service delete button or data export in the product, so these requests are handled
            by hand. We do not discriminate against anyone for using these rights. If you are in
            California, we do not sell or “share” personal information as those terms are
            defined in the CCPA.
          </p>
          <p>
            To remove a visitor’s click data (for example, you clicked a tracked link), the
            records hold only hashed identifiers, so we may need you to tell us roughly when and
            which link, and we may not be able to find it. If you think we have handled your data
            unlawfully, you can complain to your local data-protection authority (in the UK, the
            ICO). We would appreciate the chance to put it right first.
          </p>
        </LegalSection>

        <LegalSection title="8. Security">
          <p>
            Passwords are hashed with bcrypt. API keys are stored only as hashes and are shown once.
            Visitor identifiers in click records are keyed hashes. Sign-in cookies are HTTP-only and
            Secure in production, and card and bank data stay with Stripe. No system is perfectly
            secure. If a breach affects your personal data, we will tell you and the regulators as
            the law requires.
          </p>
        </LegalSection>

        <LegalSection title="9. Children">
          <p>
            Affily is for people aged 18 or over. We do not knowingly collect data from children. If
            you believe a child has given us data, email <ContactLink /> and we will delete it.
          </p>
        </LegalSection>

        <LegalSection title="10. Changes">
          <p>
            If we change this policy, the “last updated” date above will change, and for material
            changes we will email account holders or show a notice on the site.
          </p>
        </LegalSection>

        <LegalSection title="11. Contact">
          <p>
            Questions or requests about your data: <ContactLink />. {LEGAL.entityName}.
          </p>
        </LegalSection>
      </LegalPage>
    </>
  );
}
